/**
 * PublicRequestForm — Formulaire public de dépôt de demandes (sans compte).
 *
 * Accessible via l'URL `?demande=1` (ou `?demande=<idClub>` pour pré-sélectionner
 * un club). Rendu AVANT l'authentification dans AppV2 : aucune session requise.
 *
 * Les demandes partent vers /api/public/request et arrivent dans ep:requests,
 * donc dans la page « Demandes clubs » de l'outil. Les pièces jointes transitent
 * par /api/public/upload. La liste des clubs vient de /api/public/clubs.
 *
 * Aucune donnée n'est lue publiquement : le formulaire n'écrit que.
 */
import React, { useState, useEffect, useRef } from "react";

const MAX_FILES = 6;
const MAX_FILE_MB = 10;

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export default function PublicRequestForm() {
  const [clubs, setClubs] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    subject: "",
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    description: "",
    website: "", // honeypot (doit rester vide)
  });
  const [selectedClubs, setSelectedClubs] = useState(() => new Set());
  const [toDirection, setToDirection] = useState(false);
  const [assigneeId, setAssigneeId] = useState(""); // destinataire nominatif (optionnel)
  const [links, setLinks] = useState([""]);
  const [attachments, setAttachments] = useState([]); // [{name,url,size}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [done, setDone] = useState(null); // { reference, emailed }
  const fileRef = useRef(null);

  // Clubs + pré-sélection éventuelle via ?demande=<idClub>.
  useEffect(() => {
    let alive = true;
    fetch("/api/public/clubs")
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const list = d.clubs || [];
        setClubs(list);
        try {
          const pre = new URLSearchParams(window.location.search).get("demande");
          if (pre && pre !== "1" && list.some((c) => String(c.id) === String(pre))) {
            setSelectedClubs(new Set([String(pre)]));
          }
        } catch {}
      })
      .catch(() => setClubs([]));
    return () => { alive = false; };
  }, []);

  // Membres de l'équipe (pour le destinataire nominatif optionnel) : prénom + club.
  useEffect(() => {
    let alive = true;
    fetch("/api/public/members")
      .then((r) => r.json())
      .then((d) => { if (alive) setMembers(d.members || []); })
      .catch(() => setMembers([]));
    return () => { alive = false; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Membres réellement adressables selon le(s) destinataire(s) choisi(s) : on ne
  // propose que les personnes rattachées à un club sélectionné (et, si la direction
  // est cochée, les personnes rattachées au national « France »).
  const isDirectionMember = (m) => String(m.clubName || "").toLowerCase() === "france";
  const scopedMembers = members.filter((m) => {
    const inSelectedClub = m.clubId != null && selectedClubs.has(String(m.clubId));
    return inSelectedClub || (toDirection && isDirectionMember(m));
  });

  // Options "Prénom — Club" ; on ajoute le rôle seulement en cas d'homonyme même club.
  const memberOptions = (() => {
    const base = scopedMembers.map((m) => ({
      id: String(m.id),
      label: `${m.firstName}${m.clubName ? ` — ${m.clubName}` : ""}`,
      role: m.role,
    }));
    const counts = base.reduce((acc, o) => { acc[o.label] = (acc[o.label] || 0) + 1; return acc; }, {});
    return base.map((o) => (counts[o.label] > 1 && o.role ? { ...o, label: `${o.label} (${o.role})` } : o));
  })();

  // Si l'assigné choisi n'est plus dans le périmètre (changement de club/direction),
  // on le réinitialise pour ne pas garder un destinataire hors périmètre.
  useEffect(() => {
    if (assigneeId && !memberOptions.some((o) => o.id === String(assigneeId))) {
      setAssigneeId("");
    }
  }, [assigneeId, memberOptions]);

  function toggleClub(id) {
    setSelectedClubs((prev) => {
      const next = new Set(prev);
      const s = String(id);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  }

  function setLink(i, v) {
    setLinks((prev) => prev.map((l, idx) => (idx === i ? v : l)));
  }
  function addLink() {
    setLinks((prev) => (prev.length >= 10 ? prev : [...prev, ""]));
  }
  function removeLink(i) {
    setLinks((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleFiles(e) {
    const picked = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!picked.length) return;
    const room = MAX_FILES - attachments.length;
    if (room <= 0) { setError(`Maximum ${MAX_FILES} pièces jointes.`); return; }
    const tooBig = picked.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig) { setError(`« ${tooBig.name} » dépasse ${MAX_FILE_MB} Mo.`); return; }
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      picked.slice(0, room).forEach((f) => fd.append("files", f));
      const res = await fetch("/api/public/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "upload");
      setAttachments((prev) => [...prev, ...(data.files || [])]);
    } catch (err) {
      setError(
        err.message === "type_non_autorise"
          ? "Type de fichier non autorisé (images, PDF, Word, Excel, zip, texte)."
          : "Échec de l'envoi d'une pièce jointe. Réessayez."
      );
    } finally {
      setUploading(false);
    }
  }

  function removeAttachment(i) {
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));
  }

  function validate() {
    const fe = {};
    if (!form.subject.trim()) fe.subject = true;
    if (!form.firstName.trim()) fe.firstName = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) fe.email = true;
    if (!form.description.trim()) fe.description = true;
    if (selectedClubs.size === 0 && !toDirection) fe.destinataire = true;
    setFieldErrors(fe);
    return Object.keys(fe).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!validate()) {
      setError("Merci de compléter les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        subject: form.subject,
        firstName: form.firstName,
        lastName: form.lastName,
        company: form.company,
        email: form.email,
        phone: form.phone,
        description: form.description,
        website: form.website, // honeypot
        clubs: Array.from(selectedClubs),
        toDirection,
        assigneeId: assigneeId || null,
        links: links.map((l) => l.trim()).filter(Boolean),
        attachments,
      };
      const res = await fetch("/api/public/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.status === 429) throw new Error("Trop de demandes envoyées. Patientez quelques minutes.");
      if (!res.ok) throw new Error("Une erreur est survenue. Vérifiez les champs et réessayez.");
      setDone({ reference: data.reference, emailed: data.emailed });
    } catch (err) {
      setError(err.message || "Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Écran de confirmation ── */
  if (done) {
    return (
      <div className="pubform">
        <div className="pubform__card pubform__card--confirm">
          <div className="pubform__check" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="15" stroke="currentColor" strokeWidth="2" />
              <path d="M10 16.5l4 4 8-9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="pubform__confirm-title">Demande envoyée</h1>
          <p className="pubform__confirm-text">
            Merci, votre demande a bien été enregistrée. Conservez votre référence :
          </p>
          <div className="pubform__ref">{done.reference}</div>
          <p className="pubform__confirm-note">
            {done.emailed
              ? "Un accusé de réception vient de vous être envoyé par email."
              : "Vous recevrez un accusé de réception par email sous peu."}
          </p>
          <button className="pubform__btn pubform__btn--ghost" onClick={() => window.location.reload()}>
            Déposer une autre demande
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pubform">
      <div className="pubform__card">
        {/* En-tête */}
        <div className="pubform__head">
          <div className="pubform__logo" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
              <path d="M4 19L9 5L14 19" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6 14h6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
              <circle cx="19" cy="15" r="3.5" fill="#fff" />
            </svg>
          </div>
          <h1 className="pubform__title">Déposer une demande</h1>
          <p className="pubform__sub">
            Transmettez votre besoin aux clubs Esprit Padel ou à la direction communication. Aucun compte n'est nécessaire.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Sujet */}
          <div className="pubform__group">
            <label className="pubform__label" htmlFor="pf-subject">Sujet de la demande <span className="pubform__req">*</span></label>
            <input id="pf-subject" className={`pubform__input${fieldErrors.subject ? " is-error" : ""}`}
              value={form.subject} onChange={set("subject")} maxLength={200} placeholder="Ex. Affiche pour un tournoi" />
          </div>

          {/* Identité */}
          <div className="pubform__row">
            <div className="pubform__group">
              <label className="pubform__label" htmlFor="pf-first">Prénom <span className="pubform__req">*</span></label>
              <input id="pf-first" className={`pubform__input${fieldErrors.firstName ? " is-error" : ""}`}
                value={form.firstName} onChange={set("firstName")} maxLength={80} autoComplete="given-name" />
            </div>
            <div className="pubform__group">
              <label className="pubform__label" htmlFor="pf-last">Nom</label>
              <input id="pf-last" className="pubform__input" value={form.lastName} onChange={set("lastName")} maxLength={80} autoComplete="family-name" />
            </div>
          </div>

          <div className="pubform__group">
            <label className="pubform__label" htmlFor="pf-company">Entreprise / organisme <span className="pubform__opt">(si applicable)</span></label>
            <input id="pf-company" className="pubform__input" value={form.company} onChange={set("company")} maxLength={120} autoComplete="organization" />
          </div>

          <div className="pubform__row">
            <div className="pubform__group">
              <label className="pubform__label" htmlFor="pf-email">Adresse email <span className="pubform__req">*</span></label>
              <input id="pf-email" type="email" className={`pubform__input${fieldErrors.email ? " is-error" : ""}`}
                value={form.email} onChange={set("email")} maxLength={160} autoComplete="email" placeholder="vous@exemple.fr" />
            </div>
            <div className="pubform__group">
              <label className="pubform__label" htmlFor="pf-phone">Téléphone</label>
              <input id="pf-phone" className="pubform__input" value={form.phone} onChange={set("phone")} maxLength={40} autoComplete="tel" />
            </div>
          </div>

          {/* Destinataires */}
          <div className="pubform__group">
            <label className="pubform__label">Destinataire(s) <span className="pubform__req">*</span></label>
            <p className="pubform__hint">Sélectionnez un ou plusieurs clubs, ou la direction. La demande reste commune aux destinataires choisis.</p>
            <div className={`pubform__dest${fieldErrors.destinataire ? " is-error" : ""}`}>
              {clubs.map((c) => {
                const active = selectedClubs.has(String(c.id));
                return (
                  <button type="button" key={c.id}
                    className={`pubform__chip${active ? " is-active" : ""}`}
                    onClick={() => toggleClub(c.id)} aria-pressed={active}>
                    <span className="pubform__chip-dot" style={{ background: c.color }} />
                    {c.name}
                  </button>
                );
              })}
              <button type="button"
                className={`pubform__chip pubform__chip--dir${toDirection ? " is-active" : ""}`}
                onClick={() => setToDirection((v) => !v)} aria-pressed={toDirection}>
                <span className="pubform__chip-dot" style={{ background: "#182231" }} />
                Direction Esprit Padel
              </button>
            </div>
          </div>

          {/* Destinataire nominatif (optionnel) — liste déroulante prénom + club */}
          {memberOptions.length > 0 && (
            <div className="pubform__group">
              <label className="pubform__label" htmlFor="pf-assignee">
                À l'attention de <span className="pubform__opt">(optionnel)</span>
              </label>
              <p className="pubform__hint">Si tu sais qui doit traiter la demande, choisis la personne ici.</p>
              <select
                id="pf-assignee"
                className="pubform__input pubform__select"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
              >
                <option value="">— Personne en particulier (à répartir) —</option>
                {memberOptions.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div className="pubform__group">
            <label className="pubform__label" htmlFor="pf-desc">Description du besoin <span className="pubform__req">*</span></label>
            <textarea id="pf-desc" className={`pubform__input pubform__textarea${fieldErrors.description ? " is-error" : ""}`}
              value={form.description} onChange={set("description")} maxLength={5000} rows={5}
              placeholder="Décrivez votre demande : contexte, échéance souhaitée, détails utiles…" />
          </div>

          {/* Liens */}
          <div className="pubform__group">
            <label className="pubform__label">Lien(s) <span className="pubform__opt">(document, visuel, ressource…)</span></label>
            {links.map((l, i) => (
              <div className="pubform__link-row" key={i}>
                <input className="pubform__input" value={l} onChange={(e) => setLink(i, e.target.value)}
                  placeholder="https://…" inputMode="url" />
                {links.length > 1 && (
                  <button type="button" className="pubform__icon-btn" onClick={() => removeLink(i)} aria-label="Retirer ce lien">×</button>
                )}
              </div>
            ))}
            {links.length < 10 && (
              <button type="button" className="pubform__add-link" onClick={addLink}>+ Ajouter un lien</button>
            )}
          </div>

          {/* Pièces jointes */}
          <div className="pubform__group">
            <label className="pubform__label">Pièce(s) jointe(s) <span className="pubform__opt">(max {MAX_FILES} fichiers, {MAX_FILE_MB} Mo chacun)</span></label>
            <input ref={fileRef} type="file" multiple className="pubform__file-input" id="pf-files"
              onChange={handleFiles} disabled={uploading || attachments.length >= MAX_FILES}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" />
            <label htmlFor="pf-files" className={`pubform__file-btn${uploading || attachments.length >= MAX_FILES ? " is-disabled" : ""}`}>
              {uploading ? "Envoi en cours…" : "Choisir des fichiers"}
            </label>
            {attachments.length > 0 && (
              <ul className="pubform__files">
                {attachments.map((a, i) => (
                  <li key={i} className="pubform__file">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M3 1.5h5L11 4.5V12.5H3z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                      <path d="M8 1.5V5h3" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                    </svg>
                    <span className="pubform__file-name">{a.name}</span>
                    <span className="pubform__file-size">{fmtSize(a.size)}</span>
                    <button type="button" className="pubform__icon-btn" onClick={() => removeAttachment(i)} aria-label="Retirer">×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Honeypot anti-spam — invisible, ne pas remplir */}
          <div className="pubform__hp" aria-hidden="true">
            <label>Ne pas remplir
              <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
            </label>
          </div>

          {error && <div className="pubform__error">{error}</div>}

          <button type="submit" className="pubform__btn" disabled={submitting || uploading}>
            {submitting ? "Envoi…" : "Envoyer la demande"}
          </button>
          <p className="pubform__legal">
            Les informations transmises servent uniquement au traitement de votre demande par Esprit Padel.
          </p>
        </form>
      </div>
    </div>
  );
}
